import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { H1, H2, Body } from '../components/ui/Typography';
import ArticleStructuredData from '../components/common/ArticleStructuredData';

export default function PressRelease() {
  const title = "RentFi — Revolutionizing Real Estate Investment with Blockchain Technology";
  const description = "RentFi announces the launch of its token on February 5, 2025, introducing a groundbreaking approach to real estate investment through blockchain technology.";
  const publishDate = "2025-02-01T00:00:00+00:00";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content="RentFi launch, RENT token, blockchain real estate, property investment, passive income, token launch" />
        <link rel="canonical" href="https://rentfi.io/press/revolutionizing-real-estate-investment" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content="https://rentfi.io/press/revolutionizing-real-estate-investment" />
        <meta property="og:image" content="https://i.postimg.cc/hPNFtcr4/rentfi-logo.png" />
        <meta property="article:published_time" content={publishDate} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RentFi_io" />
        <meta name="twitter:creator" content="@RentFi_io" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://i.postimg.cc/hPNFtcr4/rentfi-logo.png" />
        
        {/* Article-specific Meta Tags */}
        <meta name="article:section" content="Press Releases" />
        <meta name="article:tag" content="Token Launch, Real Estate Investment, Blockchain Technology, REIT" />
      </Helmet>

      <ArticleStructuredData
        title={title}
        description={description}
        publishDate={publishDate}
      />

      <main className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/press"
              className="inline-flex items-center text-primary hover:text-primary-dark mb-8 group"
              aria-label="Return to press releases list"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Press Releases
            </Link>

            <article className="bg-white rounded-xl shadow-lg p-8">
              <header className="mb-8">
                <time dateTime="2025-02-01" className="text-gray-500 mb-4 block">February 1, 2025</time>
                <H1 className="text-primary mb-4">RentFi — Revolutionizing Real Estate Investment with Blockchain Technology</H1>
                <address className="text-gray-500 italic mb-8 not-italic">ROAD TOWN, BVI</address>
              </header>

              <div className="prose max-w-none">
                <Body className="text-gray-600 mb-6">
                  RentFi, the first Real Estate Investment Trust on blockchain, announced the launch of its token on February 5, 2025. RentFi integrates blockchain technology to make real estate investment accessible, offering rental income distribution without high entry costs, legal complexities, or management responsibilities of traditional property ownership.
                </Body>

                <Body className="text-gray-600 mb-6">
                  The platform is transforming real estate investment by offering global investors access to rental income through their $RENT tokens. Combining the stability of real estate with the efficiency of blockchain technology, RentFi helps individuals benefit from rental yields without the traditional complexities of property ownership.
                </Body>

                <Body className="text-gray-600 mb-6">
                  Through $RENT tokens, the project provides holders with exposure to diversified real estate returns. With a unique 50/50 mechanism, RentFi distributes 50% of rental income to holders for passive income, and the other 50% is allocated to buybacks and burns, promoting scarcity and value appreciation for token holders.
                </Body>

                <Body className="text-gray-600 mb-6">
                  "Real estate has long been a stable and profitable asset class, but traditional investment methods come with high barriers to entry. RentFi removes these obstacles, allowing anyone to benefit from rental property income without the common complexities of ownership," said the RentFi Foundation.
                </Body>

                <H2 className="text-primary mb-4">Key Features</H2>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li className="text-gray-600">Token holders benefit from rental yields across a portfolio of properties, reducing risks tied to single-property investments</li>
                  <li className="text-gray-600">Regular token buybacks and burns decrease the circulating supply, raising token value over time</li>
                  <li className="text-gray-600">Built on Solana, the platform ensures fast, low-cost transactions, further improving accessibility and scalability</li>
                  <li className="text-gray-600">With a capped supply of 100 million tokens, RentFi's deflationary design ensures long-term value for its community</li>
                </ul>

                <Body className="text-gray-600 mb-6">
                  "The popularity of blockchain technology has created new ways to democratize investments. RentFi takes full advantage of this by combining real estate's stability with the efficiency and transparency of the blockchain, offering investors a scalable and flexible opportunity to generate passive income," stated the RentFi Foundation.
                </Body>

                <Body className="text-gray-600 mb-6">
                  RentFi's model opens real estate markets traditionally dominated by institutions and high-net-worth individuals. "Our mission is to democratize real estate investment and provide a secure, scalable ecosystem for everyone," the Foundation explained. "From automated income distribution to simple token trading, we're creating an inclusive platform that's both intuitive and transparent."
                </Body>

                <Body className="text-gray-600 mb-6">
                  RentFi will officially launch their token on February 5, 2025. For more information and to get involved, visit{' '}
                  <a 
                    href="https://rentfi.io" 
                    className="text-secondary hover:text-secondary-dark"
                    aria-label="Visit RentFi's official website for more information"
                  >
                    rentfi.io
                  </a>{' '}
                  and follow their social media:{' '}
                  <a 
                    href="https://x.com/RentFi_io" 
                    className="text-secondary hover:text-secondary-dark"
                    aria-label="Follow RentFi on X (Twitter) for updates"
                  >
                    https://x.com/RentFi_io
                  </a>
                </Body>

                <H2 className="text-primary mb-4">About RentFi</H2>
                <Body className="text-gray-600 mb-6">
                  RentFi Limited is the REIT of the Blockchain, pioneering a new era of real estate investment. Embracing blockchain technology, RentFi simplifies property investment, providing token holders with consistent income and growth opportunities. With its innovative mechanisms and community-driven approach, RentFi aims to redefine the intersection of real estate and blockchain.
                </Body>

                <H2 className="text-primary mb-4">Media Contact</H2>
                <Body className="text-gray-600">
                  Block Consulting —{' '}
                  <a 
                    href="https://blockconsulting.cc" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary hover:text-secondary-dark"
                    aria-label="Visit Block Consulting's website"
                  >
                    https://blockconsulting.cc
                  </a>
                </Body>
              </div>
            </article>
          </motion.div>
        </div>
      </main>
    </>
  );
}